function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Table, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import 'react-virtualized/styles.css';
import VirtualizedTableHeader from './TableHeader';
import buildAddNewRowRow from './buildAddNewRowRow';
import useRowGetter from './rowGetter';
import tableMethods from './tableMethods';
import { getColumnWidths } from './getColumnWidths';
import { v4 as uuid } from 'uuid';
export default function VirtualizedTable(_ref) {
  let {
    headers,
    rows,
    showControls = true,
    onEdit,
    onDelete,
    onSort,
    onFilter,
    onAddRow = null,
    disableDelete,
    isImmutableUnderReview = false,
    headerHeightPx = 80,
    overscanRowCount = 5,
    // how many rows to render in advance
    tableClassName = '',
    textFilteringQueryParamName,
    // required for the ability to send a url with preexisting filters in the query
    headerOverflow = 'hidden'
  } = _ref;
  const [sortKey, setSortKey] = useState(0); // Dummy value to trigger re-rendering of the table on sort (not on filter)
  const {
    t
  } = useTranslation();
  const addNewRowRow = useMemo(() => buildAddNewRowRow({
    isImmutableUnderReview,
    onAddRow,
    t
  }), [isImmutableUnderReview, onAddRow, t]);
  const [textFilters, setTextFilters, mapHeaders] = tableMethods.useTextFiltering(textFilteringQueryParamName);

  // const mapHeaders = headers => [...headers].map(h => ({...h, order: h.order || null }))
  const [tableRows, setTableRows] = useState(onAddRow ? [addNewRowRow, ...rows] : [...rows]);
  const [tableHeaders, setTableHeaders] = useState(mapHeaders(showControls ? [...headers, {
    text: '',
    width: 80
  }] : headers));
  if (tableHeaders.some(h => h.sortable)) {
    if (tableRows.some(r => !r.key)) {
      throw new Error('If the table has sortable headers, its rows must have a key field and this key should be a unique value based on the rendered data.');
    }
  }
  const columnSortFunction = onSort ? onSort : tableMethods.useSortRows({
    tableHeaders,
    tableRows,
    setTableRows
  });
  const filterRowsByText = tableMethods.useTextFilteringMethod({
    tableHeaders,
    setTableHeaders,
    rows,
    setTableRows,
    textFilters,
    setTextFilters,
    onFilter
  });
  useEffect(() => {
    setTableRows(onAddRow ? [addNewRowRow, ...rows] : [...rows]);
    setTableHeaders(mapHeaders(showControls ? [...headers, {
      text: '',
      width: 80
    }] : headers));
  }, [rows, headers, onAddRow, isImmutableUnderReview, t, addNewRowRow, showControls, mapHeaders]);

  // Process rows to insert expanded child rows underneath parent rows
  const rowsWithExpandedRows = useMemo(() => {
    const processedRows = [];
    tableRows.forEach(row => {
      processedRows.push(row);
      // If row is expanded and has expandedRows array, insert those rows underneath
      if (row.expandable && row.expanded && row.expandedRows) {
        processedRows.push(...row.expandedRows);
      }
    });
    return processedRows;
  }, [tableRows]);
  const setTableHeadersSortOrder = index => {
    tableHeaders[index].order = tableHeaders[index].order === 'asc' ? 'desc' : 'asc';
    tableHeaders.forEach((h, i) => {
      if (i !== index) {
        h.order = null;
      }
    });
    setTableHeaders([...tableHeaders]);
    return tableHeaders[index].order;
  };
  const cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 20,
    maxHeight: 150
  });
  const rowGetter = useRowGetter({
    tableRows: rowsWithExpandedRows,
    tableShowControls: showControls,
    tableIsImmutableUnderReview: isImmutableUnderReview,
    tableOnDelete: onDelete,
    tableOnEdit: onEdit,
    tableDisableDelete: disableDelete
  });
  const onClickHeader = headerIndex => {
    const tableHeader = tableHeaders[headerIndex];
    setTableHeadersSortOrder(headerIndex);
    if (typeof columnSortFunction === 'function') {
      columnSortFunction(headerIndex, tableHeader.order, tableHeader.text);
    }
    setTableHeaders([...tableHeaders]);
    setSortKey(prevKey => prevKey + 1); // Update sort key to force re-render
  };

  // Put the table inside a container that has some height defined in whatever way.
  // Otherwise the table will collapse.
  return /*#__PURE__*/React.createElement("div", {
    style: {
      height: 'inherit'
    }
  }, /*#__PURE__*/React.createElement("style", null, "\n          /* These defaults are added here with classes, so that you can override them for your specific table. */\n          / * Be careful when using vertical margins in the data rows, as they are flaky - because of the virtualized table. */.\n\n          .ReactVirtualized__Table__headerRow {\n            text-transform: capitalize;\n            margin-bottom: 10px;\n          }\n\n          .ReactVirtualized__Table__headerColumn {\n            color: #666e7e;\n            font-weight: 600;\n            font-size: 1.05rem;\n          }\n\n          /* the below margin left and right are done so that the row bottom margin that we may want to have between rows\n            appear as a continuous line */\n          .ReactVirtualized__Table__rowColumn:first-of-type {\n            margin-left: 0px;\n          }\n\n          .ReactVirtualized__Table__rowColumn {\n            margin-right: 0px !important;\n            height: 100%;\n          }\n\n          .Table-Cell-Content {\n            padding: 9px;\n            font-size: 16px;\n            font-weight: 400;\n            height: 100%;\n            display: flex;\n            flex-direction: column;\n          }\n          \n          .ReactVirtualized__Table__rowColumn:first-child .Table-Cell-Content-expanded {\n            padding-top: 0px;\n            padding-bottom: 0px;\n          }\n\n          .ReactVirtualized__Table__rowColumn:first-child .Table-Cell-Content-expanded {\n            padding-top: 0px;\n            padding-bottom: 0px;\n          }\n\n          /* set height to define margin between rows */\n          /* otherwise vertical margins are flaky and may not work properly at all */\n          .Table-Cell-Spacer {\n            /* simply the color between the rows */\n            background-color: white;\n            /* margin left negative is required so that the vertical margin between rows is continuous throuugh the whole row - and does not break up where the columns end */\n            margin-left: -10px;\n            margin-right: -10px;\n            margin-top: auto;\n          }\n          .\n        "), /*#__PURE__*/React.createElement(AutoSizer, {
    key: sortKey
  }, _ref2 => {
    let {
      width,
      height
    } = _ref2;
    const allColumnWidths = getColumnWidths({
      totalWidth: width,
      tableHeaders
    });
    return /*#__PURE__*/React.createElement(Table, {
      className: "Ayming-Virtualized-Table ".concat(tableClassName),
      width: width,
      height: height,
      headerHeight: headerHeightPx,
      rowHeight: cache.rowHeight,
      rowCount: rowsWithExpandedRows.length,
      rowGetter: rowGetter,
      deferredMeasurementCache: cache,
      overscanRowCount: overscanRowCount // Adjust based on your needs but try to keep below 10
    }, tableHeaders.map((header, index) => /*#__PURE__*/React.createElement(Column, {
      columnData: _objectSpread(_objectSpread({}, header), {}, {
        sortDirection: header.order,
        headerIndex: index,
        onClickHeader,
        onFilterByText: filterRowsByText,
        headerHeight: "".concat(headerHeightPx, "px"),
        headerOverflow: headerOverflow
      }),
      headerRenderer: VirtualizedTableHeader,
      key: index,
      dataKey: index.toString(),
      width: allColumnWidths[index] || allColumnWidths.dynamicColumnWidth //Need to use ColumnMeasurer here
      ,
      label: header.text,
      cellRenderer: _ref3 => {
        var _currentRow$data, _currentRow$data2, _nextRow$data, _currentRow$data3;
        let {
          cellData,
          rowIndex,
          columnIndex,
          parent,
          key,
          style
        } = _ref3;
        const currentRow = rowsWithExpandedRows[rowIndex];
        const isExpandedRow = currentRow === null || currentRow === void 0 || (_currentRow$data = currentRow.data) === null || _currentRow$data === void 0 || (_currentRow$data = _currentRow$data.object) === null || _currentRow$data === void 0 ? void 0 : _currentRow$data.row_expanded;
        const isExpandedChildRow = currentRow === null || currentRow === void 0 || (_currentRow$data2 = currentRow.data) === null || _currentRow$data2 === void 0 || (_currentRow$data2 = _currentRow$data2.object) === null || _currentRow$data2 === void 0 ? void 0 : _currentRow$data2.is_expanded_row_child_row;
        const isExpandedRowOrChild = isExpandedRow || isExpandedChildRow;

        // Check if this is the last expanded child row in a group
        const nextRow = rowsWithExpandedRows[rowIndex + 1];
        const isNextRowNotExpandedChild = !(nextRow !== null && nextRow !== void 0 && (_nextRow$data = nextRow.data) !== null && _nextRow$data !== void 0 && (_nextRow$data = _nextRow$data.object) !== null && _nextRow$data !== void 0 && _nextRow$data.is_expanded_row_child_row);
        const isLastExpandedChildRow = isExpandedChildRow && isNextRowNotExpandedChild;
        const object = currentRow === null || currentRow === void 0 || (_currentRow$data3 = currentRow.data) === null || _currentRow$data3 === void 0 ? void 0 : _currentRow$data3.object;
        if (object !== null && object !== void 0 && object.rnd_amount && Number(object.rnd_amount) > 200 && isLastExpandedChildRow) {
          console.log('object: ', object);
        }
        console.log('cell data object: ', object === null || object === void 0 ? void 0 : object.row_expanded, object === null || object === void 0 ? void 0 : object.is_expanded_row_child_row, _objectSpread({}, object));
        return /*#__PURE__*/React.createElement(CellMeasurer, {
          cache: cache,
          columnIndex: columnIndex,
          key: key,
          parent: parent,
          rowIndex: rowIndex
        }, /*#__PURE__*/React.createElement("div", {
          className: "Table-Cell-Container ".concat(isExpandedRowOrChild ? 'Table-Cell-Container-expanded' : '', " ").concat(isLastExpandedChildRow ? 'Table-Cell-Container-expanded-last' : ''),
          style: _objectSpread(_objectSpread({}, style), {}, {
            height: '100%'
          })
        }, /*#__PURE__*/React.createElement("div", {
          className: "Table-Cell-Content ".concat(isExpandedRowOrChild ? 'Table-Cell-Content-expanded' : '', " ").concat(isLastExpandedChildRow ? 'Table-Cell-Content-expanded-last' : '')
        }, cellData, /*#__PURE__*/React.createElement("div", {
          className: "Table-Cell-Spacer"
        }))));
      }
    })));
  }));
}